import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Star, Camera, Utensils, ShoppingCart, Users, ChefHat,
  Tag, Zap, Leaf, CreditCard, Trophy, Gift, Sparkles, Store,
  BadgeCheck, Shield, TrendingUp, Heart, MapPin, Megaphone, BarChart3
} from 'lucide-react';

const tabs = [
  { id: 'user', label: 'Utilisateurs' },
  { id: 'pro', label: 'Professionnels' },
];

// ===================== USER GUIDE DATA =====================
const POINTS_TABLE = [
  { action: "Creer une recette", points: 10, icon: ChefHat },
  { action: "Organiser un repas entre amis", points: 5, icon: Users },
  { action: "Noter un repas (4 criteres)", points: 3, icon: Star },
  { action: "Photographier un plat au restaurant", points: 5, icon: Camera },
  { action: "Photo approuvee par le restaurateur", points: 20, icon: BadgeCheck },
  { action: "Noter un plat au restaurant (detaille)", points: 3, icon: Star },
  { action: "Recette mise en favoris par un autre", points: 5, icon: Heart },
  { action: "Scanner un ticket de caisse", points: 2, icon: ShoppingCart },
  { action: "Comparer les prix", points: 1, icon: TrendingUp },
];

const LEVELS = [
  { name: "Marmiton", min: 0, max: 49 },
  { name: "Cuisinier", min: 50, max: 149 },
  { name: "Sous-Chef", min: 150, max: 299 },
  { name: "Chef", min: 300, max: 599 },
  { name: "Grand Chef", min: 600, max: 999 },
  { name: "Chef Etoile", min: 1000, max: null },
];

// ===================== PRO GUIDE DATA =====================
const PLANS = [
  { name: "Starter", price: "19", features: ["Profil visible", "Catalogue produits", "Plat du jour"] },
  { name: "Pro", price: "49", features: ["Tout Starter", "Coupons illimites", "Statistiques avancees", "Campagnes marketing"] },
  { name: "Premium", price: "99", features: ["Tout Pro", "Mise en avant offerte (5km)", "Support prioritaire", "Multi-etablissements"] },
  { name: "Enterprise", price: "149", features: ["Tout Premium", "API dediee", "Account manager", "Campagnes illimitees"] },
];

export default function GuidePage() {
  const [tab, setTab] = useState('user');

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-16 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            <Link to="/"><ArrowLeft className="w-5 h-5 text-charcoal" /></Link>
            <h1 className="font-bold text-charcoal text-lg">Guide Gusto</h1>
          </div>
          <div className="flex gap-2">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  tab === t.id ? 'bg-primary text-white' : 'bg-gray-100 text-charcoal hover:bg-gray-200'
                }`}
                data-testid={`guide-tab-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {tab === 'user' && <UserGuide />}
        {tab === 'pro' && <ProGuide />}
      </div>
    </div>
  );
}

// ===================== USER GUIDE =====================
function UserGuide() {
  return (
    <div className="space-y-6">
      {/* Points */}
      <Section icon={Star} title="Gagner des points" color="text-amber-500">
        <p className="text-sm text-muted-foreground mb-4">
          Chaque action sur Gusto vous rapporte des points. Plus vous participez, plus vous montez en niveau !
        </p>
        <div className="space-y-2">
          {POINTS_TABLE.map((p, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <p.icon className="w-4 h-4 text-primary" />
              </div>
              <span className="flex-1 text-sm text-charcoal">{p.action}</span>
              <span className="font-bold text-primary text-sm">+{p.points} pts</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Levels */}
      <Section icon={Trophy} title="Niveaux de Chef" color="text-amber-500">
        <p className="text-sm text-muted-foreground mb-4">
          Votre niveau evolue automatiquement selon vos points cumules.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {LEVELS.map((l, i) => (
            <div key={i} className="p-3 bg-gray-50 rounded-xl text-center">
              <p className="font-semibold text-charcoal text-sm">{l.name}</p>
              <p className="text-xs text-muted-foreground">{l.min} - {l.max ? l.max : '...'} pts</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Coupons */}
      <Section icon={Tag} title="Coupons & Bons Plans" color="text-rose-500">
        <div className="space-y-3 text-sm text-charcoal">
          <p>Les commercants proposent des <strong>coupons de reduction</strong> que vous pouvez acheter en ligne.</p>
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="font-medium">Comment ca marche ?</p>
            <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
              <li>Rendez-vous sur la page <Link to="/coupons" className="text-primary underline">Coupons</Link></li>
              <li>Choisissez un coupon (ex: -20% chez la boulangerie)</li>
              <li>Payez en ligne par carte bancaire</li>
              <li>Recevez un <strong>code unique</strong> (ex: GUSTO-A1B2C3D4)</li>
              <li>Presentez le code au commercant lors de votre visite</li>
            </ol>
          </div>
          <p className="text-muted-foreground">Vos coupons sont accessibles a tout moment dans l'onglet "Mes coupons".</p>
        </div>
      </Section>

      {/* Anti-Gaspi */}
      <Section icon={Leaf} title="Colis Anti-Gaspi" color="text-green-500">
        <div className="space-y-3 text-sm text-charcoal">
          <p>Les commercants proposent des <strong>colis a prix reduits</strong> pour eviter le gaspillage alimentaire.</p>
          <div className="bg-green-50 rounded-xl p-4 space-y-2">
            <p className="font-medium text-green-800">Regles importantes</p>
            <ul className="space-y-1 text-green-700">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                <span><strong>1 colis par mois</strong> maximum par personne</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                <span>Si vous ne venez pas recupérer : <strong>blocage de 3 mois</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                <span>Prix generalement entre 3 et 8 EUR</span>
              </li>
            </ul>
          </div>
          <p className="text-muted-foreground">Les colis anti-gaspi sont visibles sur la carte interactive et dans l'annuaire.</p>
        </div>
      </Section>

      {/* Meals */}
      <Section icon={Users} title="Repas entre amis" color="text-blue-500">
        <div className="space-y-3 text-sm text-charcoal">
          <p>Organisez des repas et partagez les frais facilement.</p>
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="font-medium">Fonctionnalites</p>
            <ul className="space-y-1 text-muted-foreground">
              <li className="flex items-start gap-2">
                <ShoppingCart className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span><strong>Liste de courses collaborative</strong> : chacun coche ce qu'il ramene</span>
              </li>
              <li className="flex items-start gap-2">
                <CreditCard className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span><strong>Budget partage</strong> : definissez un montant par personne</span>
              </li>
              <li className="flex items-start gap-2">
                <Star className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span><strong>Notation</strong> : notez le repas sur 4 criteres (+3 pts)</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span><strong>Chef Gusto IA</strong> : suggestion de recettes adaptees aux allergies</span>
              </li>
            </ul>
          </div>
        </div>
      </Section>

      {/* Restaurant Photos */}
      <Section icon={Camera} title="Photos & Notes au restaurant" color="text-violet-500">
        <div className="space-y-3 text-sm text-charcoal">
          <p>Photographiez et notez les plats des restaurants pour gagner des points.</p>
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="font-medium">Comment ca marche ?</p>
            <ul className="space-y-1 text-muted-foreground">
              <li className="flex items-start gap-2">
                <Camera className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                <span>Prenez en photo un plat (+5 pts, max 3/jour par resto)</span>
              </li>
              <li className="flex items-start gap-2">
                <BadgeCheck className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Si le restaurateur approuve votre photo : <strong>+20 pts bonus</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <Star className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <span>Notez en detail : gout, presentation, rapport qualite/prix (+3 pts)</span>
              </li>
            </ul>
          </div>
        </div>
      </Section>
    </div>
  );
}

// ===================== PRO GUIDE =====================
function ProGuide() {
  return (
    <div className="space-y-6">
      {/* Why Gusto */}
      <Section icon={TrendingUp} title="Pourquoi Gusto ?" color="text-primary">
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { icon: MapPin, title: "Visibilite locale", desc: "Votre commerce sur la carte et dans l'annuaire, visible par tous les utilisateurs autour de vous" },
            { icon: Users, title: "Communaute engagee", desc: "Des utilisateurs actifs qui cherchent des bons plans, des restaurants et des produits locaux" },
            { icon: Tag, title: "Coupons promotionnels", desc: "Vendez des coupons en ligne pour attirer de nouveaux clients dans votre etablissement" },
            { icon: Camera, title: "Galerie collaborative", desc: "Vos clients photographient vos plats et creent du contenu pour vous gratuitement" },
            { icon: Leaf, title: "Anti-gaspi = licence offerte", desc: "Proposez des colis anti-gaspi et Gusto peut offrir votre abonnement mensuel" },
            { icon: BarChart3, title: "Statistiques", desc: "Suivez vos ventes, vos campagnes et votre visibilite en temps reel" },
          ].map((a, i) => (
            <div key={i} className="flex gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <a.icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-charcoal text-sm">{a.title}</p>
                <p className="text-xs text-muted-foreground">{a.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Plans */}
      <Section icon={CreditCard} title="Abonnements" color="text-primary">
        <p className="text-sm text-muted-foreground mb-4">Choisissez le plan adapte a votre activite. Tous les plans incluent un profil visible dans l'annuaire.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {PLANS.map((p, i) => (
            <div key={i} className={`p-4 rounded-xl border-2 ${i === 1 ? 'border-primary bg-primary/5' : 'border-gray-200'}`}>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-2xl font-bold text-charcoal">{p.price}</span>
                <span className="text-sm text-muted-foreground">EUR/mois</span>
              </div>
              <p className="font-semibold text-charcoal text-sm mb-2">{p.name}</p>
              <ul className="space-y-1">
                {p.features.map((f, j) => (
                  <li key={j} className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <BadgeCheck className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* Coupons for pros */}
      <Section icon={Tag} title="Coupons promotionnels" color="text-rose-500">
        <div className="space-y-3 text-sm text-charcoal">
          <p>Creez des coupons de reduction pour attirer de nouveaux clients.</p>
          <div className="bg-rose-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-rose-800">Commission Gusto</span>
              <span className="font-bold text-rose-700">10%</span>
            </div>
            <p className="text-rose-700 text-xs">Pour chaque coupon vendu a 10 EUR, vous recevez 9 EUR et Gusto garde 1 EUR.</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="font-medium">Fonctionnement</p>
            <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
              <li>Creez un coupon (reduction %, montant fixe, validite, quantite max)</li>
              <li>Fixez le prix de vente du coupon</li>
              <li>Les utilisateurs achetent en ligne (paiement securise Stripe)</li>
              <li>Le client recoit un code unique</li>
              <li>Vous validez le code quand il se presente chez vous</li>
              <li>Vous recevez 90% du montant sur votre tableau de bord</li>
            </ol>
          </div>
        </div>
      </Section>

      {/* Boost */}
      <Section icon={Zap} title="Mise en avant payante" color="text-amber-500">
        <div className="space-y-3 text-sm text-charcoal">
          <p>Boostez votre visibilite dans l'annuaire et sur la carte interactive.</p>
          <div className="bg-amber-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-amber-800">Tarif</span>
              <span className="font-bold text-amber-700">1 EUR / jour / km</span>
            </div>
            <div className="text-xs text-amber-700 space-y-1">
              <p>Exemples :</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white rounded-lg p-2 text-center">
                  <p className="font-bold">35 EUR</p>
                  <p>5km x 7 jours</p>
                </div>
                <div className="bg-white rounded-lg p-2 text-center">
                  <p className="font-bold">70 EUR</p>
                  <p>10km x 7 jours</p>
                </div>
                <div className="bg-white rounded-lg p-2 text-center">
                  <p className="font-bold">300 EUR</p>
                  <p>10km x 30 jours</p>
                </div>
                <div className="bg-white rounded-lg p-2 text-center">
                  <p className="font-bold">1500 EUR</p>
                  <p>50km x 30 jours</p>
                </div>
              </div>
            </div>
          </div>
          <p className="text-muted-foreground">Votre commerce apparait en priorite avec un badge distinctif. Paiement unique, sans engagement.</p>
        </div>
      </Section>

      {/* Marketing Campaigns */}
      <Section icon={Megaphone} title="Campagnes marketing" color="text-blue-500">
        <div className="space-y-3 text-sm text-charcoal">
          <p>Envoyez des notifications ciblees aux utilisateurs de votre zone.</p>
          <div className="bg-blue-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-blue-800">Tarif</span>
              <span className="font-bold text-blue-700">0,05 EUR / contact</span>
            </div>
            <div className="text-xs text-blue-700 space-y-1">
              <p>Exemples :</p>
              <ul className="space-y-1">
                <li>100 contacts = <strong>5 EUR</strong></li>
                <li>500 contacts = <strong>25 EUR</strong></li>
                <li>1000 contacts = <strong>50 EUR</strong></li>
              </ul>
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="font-medium">Ce que vous pouvez faire</p>
            <ul className="space-y-1 text-muted-foreground">
              <li className="flex items-start gap-2">
                <Megaphone className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <span>Annoncer un evenement, une promotion ou un nouveau produit</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <span>Cibler les utilisateurs par zone geographique</span>
              </li>
              <li className="flex items-start gap-2">
                <Users className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <span>Toucher une communaute locale engagee</span>
              </li>
            </ul>
          </div>
        </div>
      </Section>

      {/* Anti-Gaspi = Free License */}
      <Section icon={Leaf} title="Anti-Gaspi = Licence offerte" color="text-green-500">
        <div className="space-y-3 text-sm text-charcoal">
          <p>
            En proposant des <strong>colis anti-gaspi</strong>, vous reduisez le gaspillage alimentaire
            ET vous pouvez obtenir votre <strong>abonnement Gusto offert</strong> !
          </p>
          <div className="bg-green-50 rounded-xl p-4 space-y-3">
            <p className="font-medium text-green-800">Comment ca marche ?</p>
            <ul className="space-y-2 text-green-700">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                <span>Creez des colis avec vos invendus (prix reduit)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                <span>Les utilisateurs reservent et viennent les chercher</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                <span>Si la valeur de vos colis du mois depasse <strong>2x votre mensualite</strong>, votre prochain mois est offert !</span>
              </li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="font-medium mb-2">Exemples concrets</p>
            <div className="space-y-2 text-muted-foreground text-xs">
              <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                <span>Plan Starter (19 EUR/mois)</span>
                <span className="font-medium text-green-600">Objectif : 38 EUR de colis/mois</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                <span>Plan Pro (49 EUR/mois)</span>
                <span className="font-medium text-green-600">Objectif : 98 EUR de colis/mois</span>
              </div>
            </div>
          </div>
          <p className="text-muted-foreground">
            C'est du gagnant-gagnant : vous ne gaspillez plus, vous attirez de nouveaux clients,
            et vous ne payez plus votre abonnement. Un commerce eco-responsable attire 3x plus de clients.
          </p>
        </div>
      </Section>

      {/* Restaurant specific */}
      <Section icon={Utensils} title="Specialites Restaurants" color="text-orange-500">
        <div className="space-y-3 text-sm text-charcoal">
          <p>Fonctionnalites specifiques pour les restaurateurs.</p>
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <Utensils className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <span><strong>Menu digital</strong> : ajoutez vos plats avec prix, photos, allergenes</span>
              </li>
              <li className="flex items-start gap-2">
                <Star className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <span><strong>Plat du jour</strong> : mettez en avant votre plat du jour visible sur la carte</span>
              </li>
              <li className="flex items-start gap-2">
                <Camera className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <span><strong>Galerie photos clients</strong> : approuvez les photos de vos clients pour votre profil</span>
              </li>
              <li className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <span><strong>Accessibilite & ambiance</strong> : renseignez PMR, terrasse, parking, wifi...</span>
              </li>
            </ul>
          </div>
        </div>
      </Section>
    </div>
  );
}

// ===================== SECTION WRAPPER =====================
function Section({ icon: Icon, title, color, children }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm" data-testid={`guide-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <h2 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
        <Icon className={`w-5 h-5 ${color}`} /> {title}
      </h2>
      {children}
    </div>
  );
}
